import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Fuel, MapPin, Calendar, Truck, CreditCard, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SLOTS = [
    "Morning (7AM-11AM)",
    "Afternoon (11AM-3PM)",
    "Evening (3PM-7PM)",
    "Night (7PM-10PM)",
];

export default function OrderForm() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [pricing, setPricing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const today = new Date().toISOString().split("T")[0];

    const [form, setForm] = useState({
        contact_name: "",
        contact_phone: "",
        contact_email: "",
        customer_type: "B2C",
        company_name: "",
        gst_number: "",
        quantity_litres: 250,
        delivery_address: "",
        landmark: "",
        city: "Vadodara",
        pincode: "",
        preferred_date: today,
        preferred_slot: SLOTS[0],
        payment_method: "Cash on Delivery",
        notes: "",
    });

    useEffect(() => {
        api.get("/pricing").then(r => setPricing(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (user) {
            setForm(f => ({
                ...f,
                contact_name: user.name || f.contact_name,
                contact_phone: user.phone || f.contact_phone,
                contact_email: user.email || f.contact_email,
                customer_type: user.customer_type || f.customer_type,
                company_name: user.company_name || f.company_name,
                gst_number: user.gst_number || f.gst_number,
            }));
        }
    }, [user]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const qty = Number(form.quantity_litres) || 0;
    const ppl = pricing?.price_per_litre ?? 92.5;
    const subtotal = qty * ppl;
    const deliveryFee = qty >= (pricing?.free_delivery_above_litres ?? 500) ? 0 : (pricing?.delivery_fee ?? 250);
    const total = subtotal + deliveryFee;

    const submit = async (e) => {
        e.preventDefault();
        if (qty < 250) {
            toast.error("Minimum order quantity is 250 litres");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form, quantity_litres: qty };
            // Strip empty optional string fields so Pydantic Optional[EmailStr] doesn't reject ""
            if (!payload.contact_email || !payload.contact_email.trim()) {
                delete payload.contact_email;
            }
            const { data } = await api.post("/orders", payload);
            toast.success(`Order placed! Ref: ${data.order_number}`);
            navigate(`/order-confirmation/${data.order_number}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-slate-50 min-h-screen py-14" data-testid="order-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-10">
                    <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-3">// Order Form</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900">Book your diesel delivery.</h1>
                    <p className="mt-3 text-slate-600">Fill in the details below. Our dispatch team will confirm via call/SMS within 30 minutes.</p>
                </div>

                <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
                    {/* Left: form fields */}
                    <div className="lg:col-span-2 space-y-6">
                        <Section icon={User} title="Contact Details">
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Full Name*">
                                    <Input required value={form.contact_name} onChange={e => set("contact_name", e.target.value)} placeholder="Your name" data-testid="order-name-input" className="rounded-sm" />
                                </Field>
                                <Field label="Phone Number*">
                                    <Input required value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder="+91 98xxxxxxxx" data-testid="order-phone-input" className="rounded-sm" />
                                </Field>
                                <Field label="Email (optional)">
                                    <Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} placeholder="you@company.com" data-testid="order-email-input" className="rounded-sm" />
                                </Field>
                                <Field label="Customer Type*">
                                    <Select value={form.customer_type} onValueChange={v => set("customer_type", v)}>
                                        <SelectTrigger data-testid="order-customer-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="B2C">Individual (B2C)</SelectItem>
                                            <SelectItem value="B2B">Business (B2B)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                {form.customer_type === "B2B" && (
                                    <>
                                        <Field label="Company Name">
                                            <Input value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="Company Pvt Ltd" data-testid="order-company-input" className="rounded-sm" />
                                        </Field>
                                        <Field label="GST Number">
                                            <Input value={form.gst_number} onChange={e => set("gst_number", e.target.value)} placeholder="24AAAAA0000A1Z5" data-testid="order-gst-input" className="rounded-sm" />
                                        </Field>
                                    </>
                                )}
                            </div>
                        </Section>

                        <Section icon={Fuel} title="Fuel Requirement">
                            <Field label={`Quantity (litres)* — Minimum 250L`}>
                                <Input required type="number" min={250} max={50000} step={10} value={form.quantity_litres} onChange={e => set("quantity_litres", e.target.value)} data-testid="order-quantity-input" className="rounded-sm font-mono-plex tab-num text-lg" />
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {[250, 500, 1000, 2000, 5000, 10000].map(q => (
                                        <button type="button" key={q} onClick={() => set("quantity_litres", q)} className="text-xs font-mono-plex px-3 py-1 border border-slate-300 hover:bg-blue-700 hover:text-white hover:border-blue-700 rounded-sm" data-testid={`quantity-preset-${q}`}>
                                            {q}L
                                        </button>
                                    ))}
                                </div>
                            </Field>
                        </Section>

                        <Section icon={MapPin} title="Delivery Address">
                            <div className="space-y-4">
                                <Field label="Full Address (with door / gate no.)*">
                                    <Textarea required value={form.delivery_address} onChange={e => set("delivery_address", e.target.value)} placeholder="Plot 34, GIDC Estate, ..." rows={3} data-testid="order-address-input" className="rounded-sm" />
                                </Field>
                                <div className="grid md:grid-cols-3 gap-4">
                                    <Field label="Landmark">
                                        <Input value={form.landmark} onChange={e => set("landmark", e.target.value)} placeholder="Near ..." data-testid="order-landmark-input" className="rounded-sm" />
                                    </Field>
                                    <Field label="City*">
                                        <Input required value={form.city} onChange={e => set("city", e.target.value)} data-testid="order-city-input" className="rounded-sm" />
                                    </Field>
                                    <Field label="Pincode*">
                                        <Input required value={form.pincode} onChange={e => set("pincode", e.target.value)} placeholder="390007" data-testid="order-pincode-input" className="rounded-sm" />
                                    </Field>
                                </div>
                            </div>
                        </Section>

                        <Section icon={Calendar} title="Delivery Schedule">
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Preferred Date*">
                                    <Input required type="date" min={today} value={form.preferred_date} onChange={e => set("preferred_date", e.target.value)} data-testid="order-date-input" className="rounded-sm" />
                                </Field>
                                <Field label="Preferred Slot*">
                                    <Select value={form.preferred_slot} onValueChange={v => set("preferred_slot", v)}>
                                        <SelectTrigger data-testid="order-slot-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {SLOTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                        </Section>

                        <Section icon={CreditCard} title="Payment Method">
                            <RadioGroup value={form.payment_method} onValueChange={v => set("payment_method", v)} className="grid sm:grid-cols-2 gap-3">
                                {["Cash on Delivery", "UPI on Delivery"].map(m => (
                                    <label key={m} className={`border rounded-sm p-4 cursor-pointer flex items-center gap-3 ${form.payment_method === m ? "border-blue-700 bg-blue-50" : "border-slate-300 bg-white"}`}>
                                        <RadioGroupItem value={m} data-testid={`payment-${m.split(" ")[0].toLowerCase()}`} />
                                        <span className="font-heading font-bold text-sm text-slate-900">{m}</span>
                                    </label>
                                ))}
                            </RadioGroup>
                            <Field label="Notes for driver (optional)" className="mt-4">
                                <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Gate access, contact person on site, tank location..." data-testid="order-notes-input" className="rounded-sm" />
                            </Field>
                        </Section>
                    </div>

                    {/* Right: sticky summary */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 bg-slate-900 text-white p-8 rounded-sm relative overflow-hidden" data-testid="order-summary">
                            <div className="absolute inset-0 hero-grid opacity-30"></div>
                            <div className="relative">
                                <div className="font-mono-plex text-xs uppercase tracking-widest text-yellow-400">// Order Summary</div>
                                {pricing?.rate_note && (
                                    <div className="mt-4 border-l-2 border-yellow-400 pl-3 py-1 text-[12px] text-slate-200 leading-relaxed" data-testid="order-rate-note">
                                        <div className="text-[10px] font-mono-plex uppercase tracking-widest text-yellow-400 mb-0.5">Rate note</div>
                                        {pricing.rate_note}
                                    </div>
                                )}
                                <div className="mt-6 space-y-4 border-b border-slate-700 pb-6">
                                    <SumLine k="Quantity" v={`${qty} L`} />
                                    <SumLine k="Rate / litre" v={`₹${ppl.toFixed(2)}`} />
                                    <SumLine k="Subtotal" v={`₹${subtotal.toFixed(2)}`} />
                                    <SumLine k="Delivery Fee" v={deliveryFee === 0 ? <span className="text-green-400">FREE</span> : `₹${deliveryFee.toFixed(2)}`} />
                                </div>
                                <div className="pt-6 flex items-center justify-between">
                                    <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-400">Total (Incl. Tax)</div>
                                    <div className="font-heading font-black text-3xl tab-num text-yellow-400">₹{total.toFixed(2)}</div>
                                </div>
                                <Button type="submit" disabled={submitting || qty < 250} className="mt-8 w-full h-12 rounded-sm bg-blue-700 hover:bg-blue-800 font-bold uppercase tracking-wider disabled:opacity-50" data-testid="order-submit-btn">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Place Order <Truck className="ml-2 w-4 h-4" /></>}
                                </Button>
                                <p className="mt-4 text-[11px] text-slate-500 leading-relaxed font-mono-plex">
                                    By placing this order you agree to Vaishali Fuels' terms of service. Rate is indicative; final invoice will reflect exact metered dispense at delivery.
                                </p>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div className="bg-white border border-slate-200 rounded-sm">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div className="w-8 h-8 bg-slate-900 flex items-center justify-center rounded-sm">
                    <Icon className="w-4 h-4 text-yellow-400" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 tracking-tight">{title}</h3>
            </div>
            <div className="p-6">{children}</div>
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={className}>
            <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}

function SumLine({ k, v }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400 uppercase tracking-wider text-xs font-mono-plex">{k}</span>
            <span className="font-heading font-bold text-white tab-num">{v}</span>
        </div>
    );
}
